"use client";

import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import type { Session } from "next-auth";
import { useSession } from "next-auth/react";

import type { BlogArticle } from "@/types";
import { errorIntoString } from "@/lib/utils";
import { ga4SaveItem } from "@/lib/analytics/ga4-ecommerce";
import {
	useGetSavedBlogsQuery,
	useSetSavedBlogsMutation,
} from "@/features/saved/savedApiInjections";

import { useToast } from "@/components/ui/use-toast";
import SignupPromptModal from "@/components/auth/SignupPromptModal";
import ArticleCard from "@/components/cards/ArticleCard";


type Props = {
	/** @deprecated Session is resolved on the client via `useSession` for edge-cacheable pages. */
	session?: Session | null;
	articles: BlogArticle[];
};

export default function ArticlesCarousel({ session: sessionProp, articles }: Props) {
	const { data: clientSession } = useSession();
	const session = sessionProp !== undefined ? sessionProp : (clientSession ?? null);
	const { toast } = useToast();
	
	// CRITICAL: Memoize the user ID to prevent query argument from changing on every render
	// This ensures RTK Query doesn't re-evaluate the query unnecessarily
	// When session is null, use a stable sentinel value that RTK Query can cache properly
	const userId = useMemo(() => {
		return session?.user?.id ?? null;
	}, [session?.user?.id]);
	
	// Only fetch if user is signed in and has a valid session token
	const hasValidSession = session?.idToken && session.idToken.trim().length > 0;
	
	// CRITICAL: Only query if session exists, has a user ID, and has a valid token
	// Using skip prevents the query from running when session is null/undefined or token isn't ready
	// This prevents infinite refetch loops and 401 errors when session is not available
	// Use a stable query argument (null instead of empty string) to prevent cache key changes
	const { data, isLoading } = useGetSavedBlogsQuery(userId ?? "", {
		skip: userId === null || !hasValidSession,
		refetchOnMountOrArgChange: false, // Prevent refetch on every mount
		refetchOnFocus: false, // Prevent refetch when window regains focus
		refetchOnReconnect: false, // Prevent refetch on network reconnect
	});
	const [setFav, { isLoading: isFavoriteUpdating }] = useSetSavedBlogsMutation();
	const [showSignupModal, setShowSignupModal] = useState(false);
	const [signupReturnUrl, setSignupReturnUrl] = useState<string | undefined>(undefined);
	
	const [isPaused, setIsPaused] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [startX, setStartX] = useState(0);
	const [currentTranslate, setCurrentTranslate] = useState(0);
	const [touchStartX, setTouchStartX] = useState(0);
	const [touchStartY, setTouchStartY] = useState(0);
	const [hasMoved, setHasMoved] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const resumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	
	// Duplicate articles for seamless infinite loop without rebuilding on every render.
	const duplicatedArticles = useMemo(() => [...articles, ...articles], [articles]);
	
	// Handle interaction - pause carousel and set timer to resume after 3 seconds
	const handleInteraction = useCallback(() => {
		setIsPaused(true);
		
		// Clear existing timeout if any
		if (resumeTimeoutRef.current) {
			clearTimeout(resumeTimeoutRef.current);
		}
		
		// Set new timeout to resume after 3 seconds
		resumeTimeoutRef.current = setTimeout(() => {
			setIsPaused(false);
			resumeTimeoutRef.current = null;
		}, 3000);
	}, []);
	
	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (resumeTimeoutRef.current) {
				clearTimeout(resumeTimeoutRef.current);
			}
		};
	}, []);
	
	// Handle mouse down for dragging
	const handleMouseDown = (e: React.MouseEvent) => {
		setIsDragging(true);
		handleInteraction();
		setStartX(e.clientX);
		if (contentRef.current) {
			const transform = window.getComputedStyle(contentRef.current).transform;
			if (transform !== 'none') {
				const matrix = new DOMMatrix(transform);
				setCurrentTranslate(matrix.m41);
			}
		}
	};
	
	// Handle mouse move for dragging
	const handleMouseMove = (e: React.MouseEvent) => {
		if (!isDragging) return;
		const x = e.clientX;
		const walk = (x - startX) * 1.5;
		if (contentRef.current) {
			contentRef.current.style.transform = `translateX(${currentTranslate + walk}px)`;
		}
	};
	
	// Handle mouse up - end dragging
	const handleMouseUp = () => {
		if (isDragging) {
			setIsDragging(false);
			handleInteraction(); // Reset the 3-second timer
		}
	};
	
	// Handle touch start for mobile dragging
	const handleTouchStart = (e: React.TouchEvent) => {
		const touch = e.touches[0];
		setTouchStartX(touch.clientX);
		setTouchStartY(touch.clientY);
		setHasMoved(false);
		setStartX(touch.clientX);
		handleInteraction(); // Pause on touch
		if (contentRef.current) {
			const transform = window.getComputedStyle(contentRef.current).transform;
			if (transform !== 'none') {
				const matrix = new DOMMatrix(transform);
				setCurrentTranslate(matrix.m41);
			}
		}
	};
	
	// Handle touch move for mobile dragging
	const handleTouchMove = (e: React.TouchEvent) => {
		if (!touchStartX && !touchStartY) return;
		
		const touch = e.touches[0];
		const deltaX = Math.abs(touch.clientX - touchStartX);
		const deltaY = Math.abs(touch.clientY - touchStartY);
		
		// Only treat as drag if horizontal movement is greater than vertical (swipe) and exceeds threshold
		const DRAG_THRESHOLD = 10; // pixels
		if (deltaX > DRAG_THRESHOLD && deltaX > deltaY) {
			if (!hasMoved) {
				setHasMoved(true);
				setIsDragging(true);
				setIsPaused(true);
			}
			e.preventDefault(); // Prevent scrolling only when actually dragging
			const x = touch.clientX;
			const walk = (x - startX) * 1.5;
			if (contentRef.current) {
				contentRef.current.style.transform = `translateX(${currentTranslate + walk}px)`;
			}
		}
	};
	
	// Handle touch end - end dragging
	const handleTouchEnd = () => {
		// If user didn't drag (just tapped), allow the click to propagate to Link
		if (!hasMoved) {
			// Reset states but don't prevent click
			setTouchStartX(0);
			setTouchStartY(0);
			setHasMoved(false);
			return;
		}
		
		// User was dragging, reset drag state
		if (isDragging) {
			setIsDragging(false);
			handleInteraction(); // Reset the 3-second timer
		}
		setTouchStartX(0);
		setTouchStartY(0);
		setHasMoved(false);
	};

	const handleFavoriteClick = useCallback(
		async (article: BlogArticle) => {
			if (!session || !hasValidSession) {
				setSignupReturnUrl(
					typeof window === "undefined"
						? undefined
						: `${window.location.pathname}${window.location.search}`,
				);
				setShowSignupModal(true);
				return;
			}

			if (data === undefined) return;
			const isFavorite = data.blogIds.find((x) => x._id === article._id);

			if (isFavorite) {
				const resp = await setFav({
					userId: session.user.id,
					blogIds: data.blogIds.filter((x) => x._id !== article._id).map((x) => x._id),
				});

				if ("error" in resp) {
					const ex = errorIntoString(resp.error);
					toast({
						variant: "destructive",
						title: `Failed to remove ${article.title} from saved`,
						description: ex,
					});

					return;
				}

				toast({ title: `${article.title} removed from saved` });
				ga4SaveItem({
					content_type: "blog",
					item_id: article._id,
					item_name: article.title,
					saved: false,
				});
				return;
			}

			const resp = await setFav({
				userId: session.user.id,
				blogIds: [...data.blogIds.map((id) => id._id), article._id],
			});

			if ("error" in resp) {
				const ex = errorIntoString(resp.error);
				toast({
					variant: "destructive",
					title: `Failed to add ${article.title} to saved`,
					description: ex,
				});
				return;
			}

			toast({ title: `${article.title} saved` });
			ga4SaveItem({
				content_type: "blog",
				item_id: article._id,
				item_name: article.title,
				saved: true,
			});
		},
		[data, hasValidSession, session, setFav, toast]
	);

	return (
		<div className="relative grid min-w-0 max-w-full grid-rows-[28rem_2rem] overflow-visible">
			<div className="relative isolate h-[28rem] min-w-0 w-full max-w-full overflow-visible">
				<div 
					ref={containerRef}
					className="relative z-0 h-full w-full overflow-x-clip overflow-y-visible select-none"
					onMouseDown={handleMouseDown}
					onMouseMove={handleMouseMove}
					onMouseUp={handleMouseUp}
					onMouseLeave={() => {
						setIsDragging(false);
						// Don't resume immediately on mouse leave, let the timer handle it
					}}
					onMouseEnter={handleInteraction}
					onClick={handleInteraction}
					onTouchStart={handleTouchStart}
					onTouchMove={handleTouchMove}
					onTouchEnd={handleTouchEnd}
					style={{ 
						cursor: isDragging ? 'grabbing' : 'grab',
						userSelect: 'none',
						WebkitUserSelect: 'none',
						touchAction: 'pan-y',
					}}
				>
					<div 
						ref={contentRef}
						className="relative z-0 flex h-full gap-4 xl:gap-6 2xl:gap-8"
						style={{
							width: 'max-content',
							animationName: isDragging ? 'none' : 'scrollLeft',
							animationDuration: '60s',
							animationTimingFunction: 'linear',
							animationIterationCount: 'infinite',
							animationPlayState: isPaused ? 'paused' : 'running',
						}}
						onDragStart={(e) => e.preventDefault()}
					>
						{duplicatedArticles.map((article, k) => (
							<div 
								key={k} 
								className="flex w-[22rem] max-w-full shrink-0"
								draggable={false}
								onClick={handleInteraction}
								onMouseDown={handleInteraction}
								onTouchStart={handleInteraction}
							>
								<ArticleCard
									data={article}
									session={session}
									intent="carousel"
									favorite={{
										isLoading,
										isUpdating: isFavoriteUpdating,
										isFavorite:
											data === undefined
												? false
												: data?.blogIds.findIndex((z) => z._id === article._id) !== -1,
										onClick: handleFavoriteClick,
									}}
								/>
							</div>
						))}
					</div>
				</div>
				<div
					aria-hidden
					className="pointer-events-none absolute -inset-y-px left-0 z-[70] hidden w-[2rem] bg-gradient-to-r from-background from-0% via-background via-[92%] to-transparent to-100% sm:block xl:w-[max(2rem,calc((100vw-1280px)/2+2rem))] 2xl:w-[max(2rem,calc((100vw-1536px)/2+2rem))]"
				/>
				<div
					aria-hidden
					className="pointer-events-none absolute -inset-y-px right-0 z-[70] hidden w-[2rem] bg-gradient-to-l from-background from-0% via-background via-[92%] to-transparent to-100% sm:block xl:w-[max(2rem,calc((100vw-1280px)/2+2rem))] 2xl:w-[max(2rem,calc((100vw-1536px)/2+2rem))]"
				/>
			</div>
			
			
			<style jsx>{`
				@keyframes scrollLeft {
					0% {
						transform: translateX(-50%);
					}
					100% {
						transform: translateX(0);
					}
				}
				
				.scrollbar-hide * {
					-webkit-user-drag: none;
					-khtml-user-drag: none;
					-moz-user-drag: none;
					-o-user-drag: none;
					user-drag: none;
				}
			`}</style>
			<SignupPromptModal
				open={showSignupModal}
				onOpenChange={setShowSignupModal}
				returnUrl={signupReturnUrl}
				title="Sign up to save this blog"
				description="Create a Techxeed account before saving this blog."
			/>
		</div>
	);
}
